import { CheckIcon, ChevronDownIcon, CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Checkbox,
  CloseButton,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Loader } from "@googlemaps/js-api-loader";
import GCPImage from "components/GCPImage";
import AddressTimeSearchBar from "components/Station/AddressTimeSearchBar";
import CarList from "components/StationV2/CarList";
import {
  addHours,
  addMinutes,
  addMonths,
  format,
  isBefore,
  set,
} from "date-fns";
import useDeepEffect from "hooks/useDeepEffect";
import { sendAppierEvent } from "lib/appier";
import { reduceCarsToTwoLevelAddZoneId } from "lib/cars";
import {
  addBufferHoursET,
  addBufferHoursST,
  getAllRangeTimes,
  getAvailableCarIdsForStation,
  getDatePickerSettingsByConfigs,
  getTimeStringsByAddMins,
} from "lib/order";
import { getStationsInRange } from "lib/station";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { DateObject } from "react-multi-date-picker";
import appStore from "store";
import api from "store/api";
import stationStore from "store/station";

const CAR_TYPE_OPTIONS = {
  sort: {
    value: "modelListSortBy",
    icon: null,
    default: "default",
    options: [
      {
        label: "default",
        value: "default",
      },
      {
        label: "sortFromLow",
        value: "sortFromLow",
      },
      {
        label: "sortFromHigh",
        value: "sortFromHigh",
      },
    ],
  },
  Station_filter_carType: {
    value: "carTypeFilter",
    icon: "car",
    default: [],
    options: [
      {
        label: "Station_filter_carType_轎車",
        value: "轎車",
      },
      {
        label: "Station_filter_carType_休旅車",
        value: "休旅車",
      },
      {
        label: "Station_filter_carType_廂型車",
        value: "廂型車",
      },
      {
        label: "Station_filter_carType_跑車",
        value: "跑車",
      },
    ],
  },
  Station_filter_power: {
    value: "powerFilter",
    icon: null,
    default: [],
    options: [
      {
        label: "Station_filter_power_汽柴油",
        value: "gasoline_vehicle",
      },
      {
        label: "Station_filter_power_電力",
        value: "electric_vehicle",
      },
    ],
  },
  Station_filter_insurance: {
    value: "insuranceFilter",
    icon: "insurance",
    default: [],
    options: [
      {
        label: "Station_filter_insurance_乙式-車碰車",
        value: "乙式-車碰車",
      },
      {
        label: "Station_filter_insurance_丙式-車輛碰撞",
        value: "丙式-車輛碰撞",
      },
      {
        label: "Station_filter_insurance_第三人責任險",
        value: "第三人責任險",
      },
    ],
  },
  Station_filter_seats: {
    value: "seatsFilter",
    icon: "seats",
    default: [],
    options: [
      {
        label: "Station_filter_seats_5",
        value: "5",
      },
      {
        label: "Station_filter_seats_7",
        value: "7",
      },
      {
        label: "Station_filter_seats_9",
        value: "9",
      },
    ],
  },
};

const filterCarTypeFunc = (cars, option) => {
  if (!option.length) return cars;
  return cars.filter((car) => {
    return option.includes(car.vehicle_size_name);
  });
};

const filterPowerFunc = (cars, option) => {
  if (!option.length) return cars;
  return cars.filter((car) => {
    return option.includes(car.vehicle_type);
  });
};

const filterInsuranceFunc = (cars, option) => {
  if (!option.length) return cars;
  return cars.filter((car) => {
    return option.some((o) => {
      return car.policies.find((policy) => policy.key === o);
    });
  });
};

const filterSeatsFunc = (cars, option) => {
  if (!option.length) return cars;

  let result5 = [];
  let result7 = [];
  let result9 = [];

  if (option.includes("5")) {
    result5 = cars.filter((car) => {
      return parseInt(car.note.seat) <= 5;
    });
  }

  if (option.includes("7")) {
    result7 = cars.filter((car) => {
      return parseInt(car.note.seat) <= 7 && parseInt(car.note.seat) >= 6;
    });
  }

  if (option.includes("9")) {
    result9 = cars.filter((car) => {
      return parseInt(car.note.seat) <= 9 && parseInt(car.note.seat) >= 8;
    });
  }

  return [...result5, ...result7, ...result9];
};

const sortCarsFunc = (cars, sortBy, carTypeFilter, specialModelString) => {
  // 在排序是 default 時，如果車輛類型包含 轎車，則把 model_name === 'Model 3' 放到最前面，
  // 如果車輛類型包含 休旅車，則把 model_name === 'Model Y' 放到最前面，
  // 如果車輛類型包含 跑車，則把 model_name === 'Model S' 放到最前面
  // 其他車輛則按照價格排序

  let resultCars = cars;

  if (sortBy === "default") {
    let otherCars = [];
    let model3Cars = [];
    let modelYCars = [];
    let modelSCars = [];

    if (carTypeFilter.includes("轎車")) {
      model3Cars = cars.filter(
        (car) => car.model_name === "Model 3" && car.version === null
      );
    }

    if (carTypeFilter.includes("休旅車")) {
      modelYCars = cars.filter(
        (car) => car.model_name === "Model Y" && car.version === "後驅"
      );
    }

    if (carTypeFilter.includes("跑車")) {
      modelSCars = cars.filter(
        (car) =>
          car.model_name === "Model S" && car.version === "New! Long Range"
      );
    }

    if (carTypeFilter.length === 0) {
      model3Cars = cars.filter(
        (car) => car.model_name === "Model 3" && car.version === null
      );
    }

    // otherCars 取排除 model3Cars, modelYCars, modelSCars 的車輛
    otherCars = cars
      .filter(
        (car) =>
          !model3Cars.includes(car) &&
          !modelYCars.includes(car) &&
          !modelSCars.includes(car)
      )
      .sort((a, b) => a.price - b.price);

    resultCars = [...model3Cars, ...modelYCars, ...modelSCars, ...otherCars];
  }

  if (sortBy === "sortFromLow") {
    resultCars = resultCars.sort((a, b) => a.price - b.price);
  }
  if (sortBy === "sortFromHigh") {
    resultCars = resultCars.sort((a, b) => b.price - a.price);
  }

  if (specialModelString) {
    let specialItem = resultCars.filter((car) => {
      return (
        `${car.brand.toLowerCase()}-${car.model_name.replace(/\s/g, "").toLowerCase()}${car.version ? `-${car.version.replace(/\s/g, "").toLowerCase()}` : ""}` ===
        specialModelString.replace(/\s/g, "")
      );
    });

    let otherItems = resultCars.filter(
      (car) =>
        `${car.brand.toLowerCase()}-${car.model_name.replace(/\s/g, "").toLowerCase()}${car.version ? `-${car.version.replace(/\s/g, "").toLowerCase()}` : ""}` !==
        specialModelString.replace(/\s/g, "")
    );

    resultCars = [...specialItem, ...otherItems];
  }

  return resultCars;
};

const FilterDrawer = ({ isOpen, options, onClose }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { specialModel, hasSpecialModel } = stationStore;

  return (
    <Drawer placement="bottom" isOpen={isOpen} onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent maxH="80vh" borderTopRadius="16px">
        <DrawerBody pt="0px" px="0px" mt="16px">
          <Flex px="16px" justifyContent="flex-end" mb="8px">
            <Flex
              w="40px"
              h="40px"
              bg="#F3F4F9"
              borderRadius="20px"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={onClose}
            >
              <CloseIcon w="14px" h="14px" />
            </Flex>
          </Flex>

          {hasSpecialModel && specialModel?.brand && (
            <>
              <Box w="100%" px="16px" pb="16px" mb="16px">
                <Text mb="8px" fontWeight="700" textStyle="notoH20">
                  {t("station_special_model_filter_label")}
                </Text>
                <Flex
                  w="100%"
                  mt="16px"
                  bg="#FFFFFF"
                  alignItems="center"
                  justifyContent="space-between"
                  cursor="pointer"
                  onClick={() => {
                    stationStore.specialModel = "";
                    stationStore.hasSpecialModel = false;
                    delete router.query["model"];
                    router.push({
                      pathname: "/station",
                      query: router.query,
                    });
                    onClose();
                  }}
                >
                  <Text textStyle="notoP16" color="#333333">
                    {`${specialModel.brand} ${specialModel.model_name}${specialModel.version ? ` ${specialModel.version}` : ""}`}
                  </Text>

                  <Checkbox
                    isChecked={true}
                    onChange={() => {
                      stationStore.hasSpecialModel = false;
                      stationStore.specialModel = "";
                      delete router.query["model"];
                      router.push({
                        pathname: "/station",
                        query: router.query,
                      });
                    }}
                  />
                </Flex>
              </Box>
              <Box w="100%" h="8px" bg="#F3F4F9" mb="16px" />
            </>
          )}

          {options.map((item, index) => {
            const value =
              item === "sort"
                ? stationStore.modelListSortBy
                : stationStore[CAR_TYPE_OPTIONS[item].value];
            const _options = CAR_TYPE_OPTIONS[item].options;

            return (
              <Box key={item}>
                <Box mb="16px" px="16px">
                  <Flex alignItems="center">
                    {CAR_TYPE_OPTIONS[item].icon && (
                      <GCPImage
                        src={`/images/icons/${CAR_TYPE_OPTIONS[item].icon}.png`}
                        w="28px"
                        h="28px"
                        mr="4px"
                      />
                    )}
                    <Text textStyle="notoH24" color="#333333">
                      {t(item)}
                    </Text>
                  </Flex>
                  {_options.map((option, _index) => {
                    return (
                      <Flex
                        h="57px"
                        borderBottom={
                          _options.length === _index + 1
                            ? "none"
                            : "1px solid #E5E5E5"
                        }
                        key={option.value}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        onClick={(e) => {
                          if (item === "sort") {
                            stationStore.modelListSortBy = option.value;
                          } else {
                          }
                        }}
                      >
                        <Text
                          textStyle="notoP16"
                          color={
                            item === "sort" && value === option.value
                              ? "#0067D8"
                              : "#333333"
                          }
                        >
                          {t(option.label)}
                        </Text>

                        {item === "sort" && (
                          <CheckIcon
                            w="16px"
                            h="16px"
                            display={value === option.value ? "block" : "none"}
                            color={
                              value === option.value ? "#0067D8" : "#333333"
                            }
                          />
                        )}

                        {item !== "sort" && (
                          <HStack key={option.value} gap="8px" mb="6px">
                            <Checkbox
                              sx={{
                                "& .chakra-checkbox__control": {
                                  _focus: {
                                    boxShadow: "none",
                                  },
                                },
                              }}
                              isChecked={stationStore[
                                CAR_TYPE_OPTIONS[item].value
                              ].includes(option.value)}
                              borderColor="#28292B"
                              onChange={(e) => {
                                sendAppierEvent("filter_click", {
                                  service_name: "租車",
                                  filter_type: t(CAR_TYPE_OPTIONS[item].value),
                                  filter_text: t(option.label),
                                  status: e.target.checked
                                    ? "checked"
                                    : "unchecked",
                                });
                                if (e.target.checked) {
                                  stationStore[CAR_TYPE_OPTIONS[item].value] = [
                                    ...new Set([
                                      ...stationStore[
                                        CAR_TYPE_OPTIONS[item].value
                                      ],
                                      option.value,
                                    ]),
                                  ];
                                } else {
                                  stationStore[CAR_TYPE_OPTIONS[item].value] =
                                    stationStore[
                                      CAR_TYPE_OPTIONS[item].value
                                    ].filter((_item) => _item !== option.value);
                                }
                              }}
                            />
                          </HStack>
                        )}
                      </Flex>
                    );
                  })}
                </Box>

                {index !== options.length - 1 && (
                  <Box w="100%" h="8px" bg="#F3F4F9" mb="16px" />
                )}
              </Box>
            );
          })}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
const Station = ({
  configs = [],
  deliveryZones,
  timeOptsUnion,
  latestOrderTimeUnion,
  cityUnion,
  cars,
  orders,
  top_models,
  instant_zone_ids,
}) => {
  const {
    selectedCity,
    selectedZone,
    isSearchAirport,
    selectedDays,
    modelsFilter,
  } = appStore;

  // console.log("configs", configs);

  const router = useRouter();
  const { t } = useTranslation();
  const {
    filteredStationZones,
    carTypeFilter,
    powerFilter,
    insuranceFilter,
    seatsFilter,
    modelListSortBy,
    listLoading,
    searchAddress,
    hasSpecialModel,
    specialModel,
  } = stationStore;

  const [isGoogleReady, setIsGoogleReady] = useState(false);
  // console.log("filteredStationZones", filteredStationZones);

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filterDrawerOptions, setFilterDrawerOptions] = useState([]);

  const [stations, setStations] = useState(
    deliveryZones.filter((zone) => zone.level_3 !== null)
  );

  const [supportCars, setSupportCars] = useState([]);

  const [filterOrders, setFilterOrders] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isAllRangeTimesLoaded, setIsAllRangeTimesLoaded] = useState(false);
  const [groupPrice, setGroupPrice] = useState([]);

  const [resultCars, setResultCars] = useState([]);

  const [center, setCenter] = useState([25.0486053, 121.5169792]);
  const [isDefaultAddress, setIsDefaultAddress] = useState(false);
  const [isFilterOrderZero, setIsFilterOrderZero] = useState(false);

  const OPEN_START = set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const OPEN_END = set(addMonths(new Date(), 3), {
    hours: 23,
    minutes: 59,
    seconds: 59,
  });

  const loader = new Loader({
    apiKey: process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY,
    libraries: ["geometry"],
  });

  const specialModelString = useMemo(() => {
    return router.query.model ? router.query.model : null;
  }, [router.query.model]);

  useEffect(() => {
    loader.load().then(() => {
      setIsGoogleReady(true);
    });
  }, []);

  useEffect(() => {
    if (deliveryZones.length === 0) return;
    setStations(deliveryZones.filter((zone) => zone.level_3 !== null));
  }, [deliveryZones]);

  useEffect(() => {
    // 清空時間選擇、設定取還車同地點
    appStore.tempSelectedStartClock = "";
    appStore.tempSelectedEndClock = "";
    appStore.isSameAddress = true;
  }, []);

  // 取得選擇日期對應車款價格（總價）
  // useEffect(() => {
  //   if (selectedDays.length !== 2) return;
  //   appStore.selectedStartClock = "09:00";
  //   appStore.selectedEndClock = "09:00";
  //   const st = new DateObject(
  //     selectedDays[0].format("YYYY/MM/DD") + " " + "09:00"
  //   );
  //   const et = new DateObject(
  //     selectedDays[1].format("YYYY/MM/DD") + " " + "09:00"
  //   );
  //   const data = {
  //     start_time: st.format("YYYY/MM/DD H:mm"),
  //     end_time: et.format("YYYY/MM/DD H:mm"),
  //   };
  //   // api.getGroupPrice(data).then((resp) => {
  //   //   setGroupPrice(
  //   //     resp.data.data.map((item) => {
  //   //       const noTaxTotal = Math.ceil(item.float_discount_amount / 1.05);
  //   //       const diffTime = et.valueOf() / 1000 - st.valueOf() / 1000;
  //   //       const avgAmount = Math.ceil((noTaxTotal / (diffTime / 60 / 60)) * 24);
  //   //       return {
  //   //         ...item,
  //   //         avgAmount: avgAmount,
  //   //       };
  //   //     })
  //   //   );
  //   // });
  // }, [selectedDays]);

  useEffect(() => {
    if (router.query.station) {
      return;
    }

    // 如果 filteredStationZones 為空，則取得中心點附近站點
    console.log("isGoogleReady", isGoogleReady);

    if (
      stations.length === 0 ||
      !isGoogleReady ||
      !(typeof window !== "undefined" && window.google?.maps)
    )
      return;
    const setDefaultStationZones = async () => {
      if (filteredStationZones.length === 0) {
        // 取得中心點附近站點
        console.log("未輸入地點，取得中心點附近站點");
        setIsDefaultAddress(true);
        const _filteredStationZones = await getStationsInRange(
          center,
          stations
        );
        stationStore.filteredStationZones = _filteredStationZones;
        stationStore.searchAddress = "台北車站";
        stationStore.placeId = "ChIJUZ-WfXKpQjQR0j4ggToD89A";
      }
    };

    setDefaultStationZones();
  }, [stations, isGoogleReady, router.query.station]);

  // 篩選需要考慮的訂單
  useEffect(() => {
    if (router.query.station) {
      return;
    }
    if (
      stations.length === 0 ||
      configs.length === 0 ||
      filteredStationZones.length === 0
    )
      return;
    appStore.configs = configs;

    let _filteredStationZones = filteredStationZones;
    const checkFilteredStationZones = async () => {
      api.getOrders().then((resp) => {
        // console.log("resp", resp);
        appStore.orders = resp.data.data;
        const supportConfigs = configs.filter((config) => {
          const filteredStationZoneIds = _filteredStationZones.map(
            (zone) => zone.id
          );
          return config.support_zones.some((zone) => {
            return filteredStationZoneIds.includes(zone.id);
          });
        });

        let _supportCars = [];
        supportConfigs.forEach((config) => {
          _supportCars = [...new Set([..._supportCars, ...config.car_ids])];
        });

        setSupportCars(_supportCars);

        const _filterOrders = resp.data.data.filter((order) => {
          return (
            isBefore(new Date(order.start_time), OPEN_END) &&
            _supportCars.includes(order.car_id)
          );
        });

        if (_filterOrders.length === 0) {
          setIsFilterOrderZero(true);
        } else {
          setIsFilterOrderZero(false);
        }

        setFilterOrders(_filterOrders);
      });
    };

    checkFilteredStationZones();
  }, [stations, configs, filteredStationZones, router.query.station]);

  const allRangeTimes = useMemo(() => {
    return getAllRangeTimes(OPEN_START, OPEN_END);
  }, []);

  useEffect(() => {
    if (cars.length === 0) return;
    const addUserSetting = async (list) => {
      const data = await Promise.all(
        list.map(async (car) => {
          car.owner_settings = configs.find(({ car_ids }) =>
            car_ids.includes(car.id)
          );
          return car;
        })
      );
      appStore.cars = data;
    };
    addUserSetting(cars);
    if (configs.length === 0) return;
    appStore.configs = configs;
    if (deliveryZones.length === 0) return;
    appStore.deliveryZones = deliveryZones;
    if (top_models.length === 0) return;
    appStore.top_models = top_models;
    if (Object.keys(cityUnion).length === 0) return;
    appStore.cityUnion = cityUnion;
  }, [cars, configs, deliveryZones, top_models, cityUnion]);

  // 填資料進 allRangeTimes
  useEffect(() => {
    if (isFilterOrderZero) {
      setIsLoading(false);
      return;
    }

    if (configs.length === 0 || filterOrders.length === 0) return;

    const setAllRangeTimes = async () => {
      const now = new DateObject();
      if (selectedDays[0] > now.add(3, "months")) {
        appStore.selectedDays = [
          new DateObject().add(2, "days"),
          new DateObject().add(4, "days"),
        ];
      }

      filterOrders
        .map((o) => {
          return {
            ...o,
            start_time: o.start_time.replace(/-/g, "/"),
            end_time: o.end_time.replace(/-/g, "/"),
          };
        })
        .forEach((o) => {
          const { start_time, end_time, car_id } = o;

          const carBufferHours =
            configs.find((config) => {
              return config.car_ids.includes(car_id);
            })?.order_interval / 60;

          const thisOrderConfig = configs.find((config) =>
            config.car_ids.includes(car_id)
          );

          // if (thisOrderConfig === undefined) {
          //   console.log("o", o);
          // }

          let orderPickupZoneId = "";
          let orderReturnZoneId = "";
          let thisPickupSupportZone = {};
          let thisReturnSupportZone = {};

          if (
            o.pickup_zone_id !== null &&
            o.pickup_zone_id !== undefined &&
            o.return_zone_id !== null &&
            o.return_zone_id !== undefined
          ) {
            orderPickupZoneId = o.pickup_zone_id;
            orderReturnZoneId = o.return_zone_id;

            thisPickupSupportZone = thisOrderConfig.support_zones.find(
              (zone) => zone.id.toString() === orderPickupZoneId.toString()
            );

            thisReturnSupportZone = thisOrderConfig.support_zones.find(
              (zone) => zone.id.toString() === orderReturnZoneId.toString()
            );
          }

          let pickup_business_start_hour = "";
          let pickup_business_end_hour = "";

          if (
            thisPickupSupportZone === undefined ||
            Object.keys(thisPickupSupportZone).length === 0
          ) {
            pickup_business_start_hour = thisOrderConfig.business_start_hour;
            pickup_business_end_hour = thisOrderConfig.business_end_hour;
          } else {
            pickup_business_start_hour =
              thisPickupSupportZone.business_start_hour;
            pickup_business_end_hour = thisPickupSupportZone.business_end_hour;
          }

          let return_business_start_hour = "";
          let return_business_end_hour = "";

          if (
            thisReturnSupportZone === undefined ||
            Object.keys(thisReturnSupportZone).length === 0
          ) {
            return_business_start_hour = thisOrderConfig.business_start_hour;
            return_business_end_hour = thisOrderConfig.business_end_hour;
          } else {
            return_business_start_hour =
              thisReturnSupportZone.business_start_hour;
            return_business_end_hour = thisReturnSupportZone.business_end_hour;
          }

          const startTime = addBufferHoursST(
            addHours(new Date(start_time), 8), // 訂單時間轉換成台北時間
            carBufferHours,
            Number(pickup_business_start_hour.split(":")[0]),
            Number(
              pickup_business_end_hour.split(":")[0] === "00"
                ? "24"
                : pickup_business_end_hour.split(":")[0]
            )
          );

          const endTime = addBufferHoursET(
            addHours(new Date(end_time), 8),
            carBufferHours,
            Number(return_business_start_hour.split(":")[0]),
            Number(
              return_business_end_hour.split(":")[0] === "00"
                ? "24"
                : return_business_end_hour.split(":")[0]
            )
          );

          let curTime = startTime;
          while (curTime <= endTime) {
            const date = format(curTime, "yyyy/MM/dd");
            const time = format(curTime, "HH:mm");
            if (allRangeTimes[date]) {
              if (!allRangeTimes[date][time]?.cars.includes(car_id)) {
                allRangeTimes[date][time]?.cars.push(car_id);
              }
              allRangeTimes[date][time]?.orders.push(o);
            }
            curTime = addMinutes(curTime, 30);
          }

          function generateTimes(baseTime, mins) {
            return mins.map((min) =>
              getTimeStringsByAddMins(new Date(baseTime), min)
            );
          }

          function updateAllRangeTimes(time) {
            const mainKey = time[0];
            const subKey = time[1];

            if (allRangeTimes[mainKey] && allRangeTimes[mainKey][subKey]) {
              if (
                allRangeTimes[mainKey][subKey].guys[thisOrderConfig.location_id]
              ) {
                allRangeTimes[mainKey][subKey].guys[
                  thisOrderConfig.location_id
                ] += 1;
              } else {
                allRangeTimes[mainKey][subKey].guys[
                  thisOrderConfig.location_id
                ] = 1;
              }
            }
          }

          const timesMins = [7, 7.5, 8, 8.5, 9];
          const startTimes = generateTimes(start_time, timesMins);
          const endTimes = generateTimes(end_time, timesMins);

          startTimes.forEach(updateAllRangeTimes);
          endTimes.forEach(updateAllRangeTimes);
        });

      // console.log("allRangeTimes:", allRangeTimes);
      setIsAllRangeTimesLoaded(true);
      appStore.allRangeTimes = allRangeTimes;
    };

    setAllRangeTimes();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, filterOrders]);

  const selectedRangeKey = useMemo(
    () =>
      selectedDays[0]?.format("YYYY/MM/DD") +
      selectedDays[1]?.format("YYYY/MM/DD"),
    [
      selectedDays[0]?.format("YYYY/MM/DD"),
      selectedDays[1]?.format("YYYY/MM/DD"),
    ]
  );

  useDeepEffect(() => {
    if (!isAllRangeTimesLoaded) return;

    // console.log("allRangeTimes", allRangeTimes);

    let carsDetail = cars.filter((car) => supportCars.includes(car.id));

    // console.log("carsDetail", carsDetail);

    if (carsDetail.length === 0) {
      setResultCars([]);

      setIsLoading(false);
      return;
    }

    const calculateAvailableCars = async () => {
      setIsLoading(true);
      stationStore.listLoading = true;

      await new Promise((r) => setTimeout(r, 0));

      const startDate = new DateObject(selectedDays[0].format("YYYY/MM/DD"));
      const endDate = new DateObject(selectedDays[1].format("YYYY/MM/DD"));

      let allAvailableCars = [];
      let allAvailableCarsByStation = [];
      let countCars = [];

      // console.log("allRangeTimes:", allRangeTimes);

      // console.log("configs", configs);
      // console.log("filteredStationZones", filteredStationZones);
      // console.log("supportCars", supportCars);
      // console.log("cars", cars);
      // 每個地點取得一次可預約車輛 ids
      filteredStationZones.map((station) => {
        const supportZoneCars = carsDetail.filter((car) =>
          car.owner_settings.support_zones.some(
            (zone) => zone.id === station.id
          )
        );

        countCars = supportZoneCars;

        // console.log("STEP 0 篩選出可以預約的車輛", countCars);

        // STEP 1: 篩選車輛類型
        countCars = filterCarTypeFunc(countCars, carTypeFilter);
        // console.log("STEP 1 篩選車輛類型", countCars);

        // STEP 2: 篩選車輛動力
        countCars = filterPowerFunc(countCars, powerFilter);
        // console.log("STEP 2 篩選車輛動力", countCars);

        // STEP 3: 篩選車輛保險
        countCars = filterInsuranceFunc(countCars, insuranceFilter);
        // console.log("STEP 3 篩選車輛保險", countCars);

        // console.log("seatsFilter", seatsFilter);
        // STEP 4: 篩選車輛座位
        countCars = filterSeatsFunc(countCars, seatsFilter);
        // console.log("STEP 4 篩選車輛座位", countCars);

        const avaiableCarIds = getAvailableCarIdsForStation(
          startDate,
          endDate,
          allRangeTimes,
          countCars.map((car) => car.id),
          configs,
          station.id
        );

        allAvailableCars = [
          ...new Set([...allAvailableCars, ...avaiableCarIds]),
        ];

        allAvailableCarsByStation = [
          ...new Set([
            ...allAvailableCarsByStation,
            ...avaiableCarIds.map((car_id) => `${car_id}_${station.id}`),
          ]),
        ];
      });

      let resultCars = allAvailableCarsByStation.map((id) => {
        return {
          ...carsDetail.find((car) => car.id === id.split("_")[0]),
          zone_id: id.split("_")[1],
        };
      });

      // console.log("modelListSortBy", modelListSortBy);
      // STEP 5: 排序
      resultCars = sortCarsFunc(
        resultCars,
        modelListSortBy,
        carTypeFilter,
        specialModelString
      );
      // console.log("STEP 5 排序", resultCars);

      resultCars = reduceCarsToTwoLevelAddZoneId(resultCars, "zh-TW");

      const resultCarsDetail = cars.filter((car) =>
        allAvailableCars.includes(car.id)
      );

      if (specialModelString) {
        const _hasSpecialModel = resultCarsDetail.some((car) => {
          return (
            `${car.brand.toLowerCase()}-${car.model_name.replace(/\s/g, "").toLowerCase()}${car.version ? `-${car.version.replace(/\s/g, "").toLowerCase()}` : ""}` ===
            specialModelString.replace(/\s/g, "")
          );
        });
        stationStore.hasSpecialModel = _hasSpecialModel;
        stationStore.specialModel = resultCarsDetail.find((car) => {
          return (
            `${car.brand.toLowerCase()}-${car.model_name.replace(/\s/g, "").toLowerCase()}${car.version ? `-${car.version.replace(/\s/g, "").toLowerCase()}` : ""}` ===
            specialModelString.replace(/\s/g, "")
          );
        });
      }

      sendAppierEvent("search_result", {
        service_name: "租車",
        has_result: Object.keys(resultCars).length.toString(),
        search_keyword: isDefaultAddress ? "預設-台北車站" : searchAddress,
        search_source: router.query.utm_source,
        user_type: appStore.member?.id
          ? appStore.member?.country_code === "886"
            ? "member"
            : "foreigner"
          : "visitor",
        filter_car_type: carTypeFilter.join(","),
        filter_power: powerFilter.join(","),
        filter_insurance: insuranceFilter.join(","),
        filter_seats: seatsFilter.join(","),
        start_date: selectedDays[0].format("YYYY/MM/DD"),
        end_date: selectedDays[1].format("YYYY/MM/DD"),
      });

      console.log("計算結束", Object.keys(resultCars).length.toString());
      setResultCars(resultCars);
      setIsLoading(false);
    };

    calculateAvailableCars();
  }, [
    configs,
    isAllRangeTimesLoaded,
    carTypeFilter,
    powerFilter,
    insuranceFilter,
    seatsFilter,
    modelListSortBy,
    supportCars,
    JSON.stringify(filteredStationZones),
    selectedRangeKey,
    specialModelString,
  ]);

  const openFilterDrawer = (type) => {
    setIsFilterDrawerOpen(true);
    if (type === "all") {
      setFilterDrawerOptions(Object.keys(CAR_TYPE_OPTIONS));
    } else {
      setFilterDrawerOptions([type]);
    }
  };

  const hasAnyFilter = useMemo(() => {
    if (
      carTypeFilter.length > 0 ||
      powerFilter.length > 0 ||
      insuranceFilter.length > 0 ||
      seatsFilter.length > 0 ||
      modelListSortBy !== "default"
    ) {
      return true;
    }
    return false;
  }, [
    carTypeFilter,
    powerFilter,
    insuranceFilter,
    seatsFilter,
    modelListSortBy,
  ]);

  if (stations.length === 0) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner />
      </Flex>
    );
  }
  return (
    <Box
      w="100vw"
      maxH={{ base: "calc(100svh - 50px)", lg: "calc(100vh - 72px)" }}
      px="16px"
      pb="150px"
      h={{ base: "calc(100svh - 50px)", lg: "calc(100vh - 72px)" }}
      overflow="hidden"
      pt="90px"
      overflowY="scroll"
    >
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        options={filterDrawerOptions}
        onClose={() => setIsFilterDrawerOpen(false)}
      />
      {/* header + 手機版篩選 */}
      <Box
        flex="1"
        w="100%"
        zIndex="998"
        pos="fixed"
        top={{ base: "50px", lg: "72px" }}
        left="0"
        right="0"
      >
        <Flex
          flexDir={{ base: "column", lg: "row" }}
          justify="center"
          align="center"
          w="100%"
          bg="white"
          border="1px solid rgba(0, 0, 0, 0.1)"
          pos="relative"
          zIndex="1"
        >
          <Flex
            w="100%"
            maxW={{ base: "90%", lg: "960px" }}
            alignItems="center"
            justify="center"
            zIndex="1"
            pos="relative"
          >
            <AddressTimeSearchBar cityUnion={cityUnion} setCenter={setCenter} />
          </Flex>

          {/* 手機版篩選 */}
          <Flex
            px="16px"
            pb="16px"
            w="100%"
            display={{ base: "flex", lg: "none" }}
          >
            <Flex
              flexShrink={0}
              mr="8px"
              w="40px"
              h="40px"
              bg={hasAnyFilter ? "#28292B" : "#F3F4F9"}
              borderRadius="8px"
              align="center"
              justify="center"
              onClick={() => openFilterDrawer("all")}
            >
              <GCPImage
                src={`/images/icons/filter${hasAnyFilter ? "_white" : ""}.png`}
                w="24px"
                h="24px"
              />
            </Flex>

            <Flex
              w="100%"
              overflowX="scroll"
              alignItems="center"
              h="40px"
              gap="8px"
            >
              {hasSpecialModel && specialModel?.brand && (
                <Flex
                  minW="104px"
                  maxW="104px"
                  py="8px"
                  pl="12px"
                  pr="10px"
                  bg="#F3F4F9"
                  borderRadius="8px"
                  alignItems="center"
                  cursor="pointer"
                  onClick={() => {
                    stationStore.specialModel = "";
                    stationStore.hasSpecialModel = false;
                    delete router.query["model"];
                    router.push({
                      pathname: "/station",
                      query: router.query,
                    });
                  }}
                >
                  <Text
                    maxW="64px"
                    mr="2px"
                    textStyle="notoH14"
                    color="#333333"
                    fontWeight="700"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {`${specialModel.brand} ${specialModel.model_name}${specialModel.version ? ` ${specialModel.version}` : ""}`}
                  </Text>
                  <GCPImage src="/images/icons/close.png" w="20px" h="20px" />
                </Flex>
              )}
              {Object.keys(CAR_TYPE_OPTIONS).map((filter_option) => {
                const value =
                  filter_option === "sort"
                    ? stationStore.modelListSortBy
                    : stationStore[CAR_TYPE_OPTIONS[filter_option].value];
                const hasFilter =
                  filter_option === "sort"
                    ? stationStore.modelListSortBy !== "default"
                    : stationStore[CAR_TYPE_OPTIONS[filter_option].value]
                        .length > 0;
                return (
                  <Flex
                    key={filter_option}
                    h="36px"
                    bg={hasFilter ? "#28292B" : "#F3F4F9"}
                    borderRadius="8px"
                    alignItems="center"
                    justifyContent="center"
                    px="12px"
                    flexShrink={0}
                    onClick={() => {
                      openFilterDrawer(filter_option);
                    }}
                  >
                    {CAR_TYPE_OPTIONS[filter_option].icon && (
                      <GCPImage
                        src={`/images/icons/${CAR_TYPE_OPTIONS[filter_option].icon}${
                          hasFilter ? "_white" : ""
                        }.png`}
                        w="16px"
                        h="16px"
                        mr="2px"
                      />
                    )}
                    <Text
                      whiteSpace="nowrap"
                      mr="4px"
                      textStyle="notoH14"
                      color={hasFilter ? "#FFFFFF" : "#333333"}
                    >
                      {t(filter_option)}
                    </Text>
                    <GCPImage
                      src={
                        hasFilter
                          ? "/images/icons/chevronDown_white.png"
                          : "/images/icons/chevronDown.png"
                      }
                      w="16px"
                      h="16px"
                    />
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Flex>
      </Box>

      <Flex
        flexDir={{ base: "flex", lg: "row" }}
        gap="24px"
        maxW="1110px"
        w="100%"
        mx={{ base: "0px", lg: "auto" }}
        mt={{ base: "56px", lg: "16px" }}
        p="0px"
        alignItems="flex-start"
        h="100%"
      >
        {/* 電腦版篩選 */}
        <Flex
          display={{ base: "none", lg: "flex" }}
          flex="2"
          borderRadius="16px"
          bg="#F3F4F9"
          p="16px"
        >
          <Box w="100%">
            {hasSpecialModel && specialModel?.brand && (
              <Box
                w="100%"
                pb="16px"
                mb="16px"
                borderBottom="1px solid rgba(0, 0, 0, 0.1)"
              >
                <Text mb="8px" fontWeight="700" textStyle="notoH16">
                  {t("station_special_model_filter_label")}
                </Text>
                <Flex
                  w="fit-content"
                  py="2px"
                  pl="8px"
                  pr="4px"
                  border="1px solid #28292B"
                  borderRadius="4px"
                  alignItems="center"
                  cursor="pointer"
                  onClick={() => {
                    stationStore.specialModel = "";
                    stationStore.hasSpecialModel = false;
                    delete router.query["model"];
                    router.push({
                      pathname: "/station",
                      query: router.query,
                    });
                  }}
                >
                  <Text
                    mr="2px"
                    textStyle="notoH14"
                    color="#333333"
                    fontWeight="700"
                  >
                    {`${specialModel.brand} ${specialModel.model_name}`}
                  </Text>
                  <GCPImage src="/images/icons/close.png" w="20px" h="20px" />
                </Flex>
              </Box>
            )}
            {Object.keys(CAR_TYPE_OPTIONS)
              .filter((item) => item !== "sort")
              .map((filter_option, index) => {
                return (
                  <Box
                    w="100%"
                    mt={index !== 0 ? "16px" : "0"}
                    alignItems="center"
                    key={filter_option}
                    borderBottom={
                      index !== Object.keys(CAR_TYPE_OPTIONS).length - 2
                        ? "1px solid rgba(0, 0, 0, 0.1)"
                        : "none"
                    }
                    pb={
                      index !== Object.keys(CAR_TYPE_OPTIONS).length - 2
                        ? "16px"
                        : "0"
                    }
                  >
                    <Flex mb="8px" alignItems="center">
                      {CAR_TYPE_OPTIONS[filter_option].icon && (
                        <GCPImage
                          src={`/images/icons/${CAR_TYPE_OPTIONS[filter_option].icon}.png`}
                          w="20px"
                          h="20px"
                          mr="2px"
                        />
                      )}
                      <Text textStyle="notoH16" color="#333333">
                        {t(filter_option)}
                      </Text>
                    </Flex>
                    {CAR_TYPE_OPTIONS[filter_option].options.map((option) => {
                      return (
                        <Flex alignItems="center" key={option.value} mb="4px">
                          <Checkbox
                            mt="1px"
                            size="sm"
                            mr="8px"
                            sx={{
                              "& .chakra-checkbox__control": {
                                _focus: {
                                  boxShadow: "none",
                                },
                              },
                            }}
                            isChecked={stationStore[
                              CAR_TYPE_OPTIONS[filter_option].value
                            ].includes(option.value)}
                            borderColor="#28292B"
                            onChange={(e) => {
                              sendAppierEvent("filter_click", {
                                service_name: "租車",
                                filter_type: t(filter_option),
                                filter_text: t(option.label),
                                status: e.target.checked
                                  ? "checked"
                                  : "unchecked",
                              });

                              if (e.target.checked) {
                                stationStore[
                                  CAR_TYPE_OPTIONS[filter_option].value
                                ] = [
                                  ...new Set([
                                    ...stationStore[
                                      CAR_TYPE_OPTIONS[filter_option].value
                                    ],
                                    option.value,
                                  ]),
                                ];
                              } else {
                                stationStore[
                                  CAR_TYPE_OPTIONS[filter_option].value
                                ] = stationStore[
                                  CAR_TYPE_OPTIONS[filter_option].value
                                ].filter((item) => item !== option.value);
                              }
                            }}
                          />
                          <Text textStyle="notoP14" color="#333333">
                            {t(option.label)}
                          </Text>
                        </Flex>
                      );
                    })}
                  </Box>
                );
              })}
          </Box>
        </Flex>

        <Flex flex="8" display="block" flexShrink={1} h="100%">
          <Flex w="100%" justify="space-between" align="center" mb="16px">
            <Text
              textStyle={{ base: "notoP14", lg: "notoP16" }}
              color="#748598"
            >
              {t("Station_distance_info")}
            </Text>

            <Menu>
              <MenuButton display={{ base: "none", lg: "block" }}>
                <Flex
                  pl="12px"
                  py="8px"
                  pr="10px"
                  borderRadius="8px"
                  bg="#F3F4F9"
                  align="center"
                >
                  <Text mr="2px" textStyle="notoH14">
                    {t("sort")}
                  </Text>
                  <ChevronDownIcon w="16px" h="16px" />
                </Flex>
              </MenuButton>

              <MenuList>
                {["default", "sortFromLow", "sortFromHigh"].map((item) => {
                  return (
                    <MenuItem
                      value={item}
                      key={item}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={(e) => {
                        stationStore.modelListSortBy = item;
                      }}
                    >
                      <Text
                        textStyle="notoP14"
                        color={modelListSortBy === item ? "#0067D8" : "#333333"}
                      >
                        {t(item)}
                      </Text>

                      <CheckIcon
                        w="16px"
                        h="16px"
                        display={modelListSortBy === item ? "block" : "none"}
                        color={modelListSortBy === item ? "#0067D8" : "#333333"}
                      />
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Menu>
          </Flex>

          {isLoading ? (
            <Stack>
              <Skeleton
                height={{ base: "203px", md: "160px" }}
                borderRadius="16px"
              />
              <Skeleton
                height={{ base: "203px", md: "160px" }}
                borderRadius="16px"
              />
              <Skeleton
                height={{ base: "203px", md: "160px" }}
                borderRadius="16px"
              />
              <Skeleton
                height={{ base: "203px", md: "160px" }}
                borderRadius="16px"
              />
              <Skeleton
                height={{ base: "203px", md: "160px" }}
                borderRadius="16px"
              />
              <Skeleton
                height={{ base: "203px", md: "160px" }}
                borderRadius="16px"
              />
            </Stack>
          ) : (
            <CarList carData={resultCars} />
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export async function getServerSideProps(context) {
  const { locale } = context;

  const settingResp = await api.getAllOwnerSetting();
  const deliveryZoneResp = await api.getDeliveryZone();
  const carsResp = await api.getCars();
  const cars = carsResp.data.data;
  const top_models = carsResp.data.top_models;
  const instant_zone_ids = carsResp.data.instant_zone_ids;
  const configs = settingResp.data.data.map((config) => ({
    ...config,
    owner_id: config.location_id || "udrive",
  }));

  const deliveryZones = deliveryZoneResp.data.data;
  const { latestOrderTime, timeOpts, deliveryZonesObj } =
    getDatePickerSettingsByConfigs(
      settingResp.data.data,
      deliveryZoneResp.data.data
    );
  const timeOptsUnion = timeOpts;
  const latestOrderTimeUnion = latestOrderTime;
  const cityUnion = deliveryZonesObj;
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      configs: configs || [],
      deliveryZones: deliveryZones || [],
      timeOptsUnion: timeOptsUnion || [],
      latestOrderTimeUnion: latestOrderTimeUnion || 0,
      cityUnion: cityUnion || {},
      cars: cars || [],
      top_models: top_models || [],
      instant_zone_ids: instant_zone_ids || [],
    },
  };
}

export default Station;
